<script setup>
import 'vue3-carousel/dist/carousel.css'
import { vElementSize } from '@vueuse/components'
import { Power4, gsap } from 'gsap'

defineProps({ blok: Object })

const params = useRoute().query
const localePath = useLocalePath()

const { categories, fetchCategories } = useNewsCategories()
const { fetchAll, get_all, loading, page } = useLearnMoreArticles()

async function initParams() {
  page.value = params.page === undefined ? 1 : params.page
}

await Promise.all([
  await initParams(),
  await fetchCategories(),
  await fetchAll(),
])

const normalizedArticles = computed(() => get_all.value
  .filter(story => story.slug !== 'index')
  .map((story) => {
    const block = story.content?.body?.[0] || story.content || {}

    return {
      ...story,
      content: {
        title: block.title,
        image: block.media,
        date: story.first_published_at || story.created_at,
      },
    }
  }))

const { t, locale } = useI18n()
const route = useRoute()
const isLearnMorePath = computed(() => route.path.includes('/learn-more'))
const activeIndex = ref(null)
const getWidth = ref(0)
const getHeight = ref(680)
const target = ref(null)
const targetIsVisible = ref(false)
const statusOptions = ref([])
const notFound = ref(false)
const status = ref(categories.value[0].name)

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting)
      targetIsVisible.value = isIntersecting
  },
)

const filteredStatus = computed(() => {
  return normalizedArticles.value.filter((obj) => {
    if (status.value === categories.value[0].name) {
      return true
    }
    else {
      for (let i = 0; i <= (obj.content.categories?.length || 0); i++) {
        const data = obj.content.categories[i]
        if (data !== undefined && data === status.value)
          return data.toLowerCase() === status.value.toLowerCase()
      }
    }
  })
})

const sortArticle = computed(() => {
  return filteredStatus.value
})

function filteredUUID() {
  statusOptions.value = []
  if (categories?.value.length) {
    for (let i = 0; i <= categories?.value.length; i++) {
      const data = categories?.value[i]

      if (data !== undefined)
        statusOptions.value.push({ id: data.uuid, value: data.name })
    }
  }
}

function onHover(index) {
  activeIndex.value = index
}

function onResize({ width, height }) {
  getWidth.value = width
  getHeight.value = height
}

function onEnter(el, done) {
  gsap.to(el, 0.1, {
    autoAlpha: 1,
    ease: Power4.easeIn,
    onComplete: done,
  }, {
    autoAlpha: 0,
    ease: Power4.easeIn,
    onComplete: done,
  })
  done()
}
function onLeave(el, done) {
  gsap.to(el, 0.1, {
    autoAlpha: 0,
    ease: Power4.easeIn,
    onComplete: done,
  }, {
    autoAlpha: 1,
    ease: Power4.easeIn,
    onComplete: done,
  })
  done()
}

function onBeforeEnter(el) {
  el.style.opacity = 0
}

async function addParams() {
  await navigateTo(localePath(
    {
      path: route.path,
      query: {
        page: page.value,
      },
    },
  ))
}

async function nextArticle() {
  notFound.value = false
  if (page.value >= 1) {
    Number(page.value--)
    if (page.value !== 0) {
      await Promise.all([
        fetchCategories(),
        fetchAll(),
      ])
      await addParams()
    }
  }
  else {
    page.value = 1
  }
}

async function previousArticle() {
  if (page.value >= 1 && sortArticle.value.length >= 1) {
    notFound.value = false
    Number(page.value++)
    await Promise.all([
      fetchCategories(),
      fetchAll(),
    ])
    await addParams()
  }
  if (sortArticle.value.length === 0)
    notFound.value = true
}

watchEffect(async () => {
  if (page.value === 0)
    page.value = 1

  if (sortArticle.value.length === 0)
    notFound.value = true
  else
    notFound.value = false
})

onMounted(() => {
  filteredUUID()
})
</script>

<template>
  <div v-if="route.name === `index___${locale}`" ref="target" class="relative bg-secondary-gray-50/70 lg:py-48 xl:py-desktop">
    <div class="absolute inset-y-0 left-0 z-0 hidden h-full w-[45%] bg-primary lg:flex">
      <div class="absolute bottom-0 left-0 origin-center overflow-hidden -z-1 2xl:min-h-[400px] 2xl:min-w-[400px] lg:min-h-[300px] lg:min-w-[300px] -scale-x-100">
        <DecorlationRstriped current-color="text-secondary-red-100" class="absolute bottom-0 left-0 z-0 hidden h-full w-auto origin-center translate-x-0 mix-blend-darken lg:flex" />
      </div>
    </div>
    <div
      :style="{
        minWidth: `${getWidth}px`,
        minHeight: `${getHeight}px`,
      }"
      class="relative z-50 max-w-full items-center lg:flex"
    >
      <div class="w-full lg:container">
        <div class="flex flex-col items-center lg:flex-row">
          <div class="w-full bg-primary py-mobile lg:w-[35%] lg:bg-transparent lg:py-0">
            <div class="absolute right-0 top-0 z-0 min-h-[200px] min-w-[200px] flex origin-center overflow-hidden lg:hidden sm:min-h-[250px] sm:min-w-[250px] -scale-x-100">
              <DecorlationRstriped current-color="text-secondary-red-100" class="absolute right-0 top-0 z-0 h-full w-auto origin-center translate-x-0 mix-blend-darken -scale-100" />
            </div>
            <div class="relative z-10 container lg:max-w-full lg:px-0">
              <div
                :class="[targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]']"
                class="transition-[transform,opacity] mb-1 text-md text-secondary-red-100 font-bold capitalize duration-700 delay-200 lg:text-xl md:text-xl sm:text-lg"
              >
                {{ blok.header }}
              </div>
              <h2
                :class="[targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]']"
                class="transition-[transform,opacity] mx-auto mb-custom-md text-3xl text-white font-bold duration-700 delay-400 md:text-4xl xl:text-5xl"
              >
                {{ blok.title }}
              </h2>
              <p
                :class="[targetIsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[-15px]']"
                class="transition-[transform,opacity] pb-custom-lg text-xl text-white leading-[1.6] duration-700 delay-600"
              >
                {{ blok.description }}
              </p>
              <div :class="[targetIsVisible ? 'opacity-100 !translate-y-0' : 'opacity-0 !translate-y-[15px]']" class="transition-[transform,opacity] duration-700 delay-800">
                <NuxtLink :to="localePath({ path: '/learn-more' })">
                  <UIButton size="xl" class="rounded-full">
                    <template #default>
                      {{ t('button.view') }}
                    </template>
                  </UIButton>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right-0 w-full pb-mobile lg:absolute -mt-18 lg:ml-auto lg:mt-0 lg:w-[58%] lg:pb-0">
        <div class="mx-0 px-3 lg:container md:px-0">
          <CarouselNewsArticleComponent v-element-size="onResize" class="transition-[transform,opacity] duration-700 delay-500 ease-in-out" :categories="categories" :is-visible="targetIsVisible" :articles="normalizedArticles" />
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="route.name === `news___${locale}` || isLearnMorePath" :class="isLearnMorePath ? 'py-mobile-navbar lg:py-desktop-navbar' : ''">
    <div :class="isLearnMorePath ? 'container pt-8 lg:pt-12' : ''">
      <div
        class="flex items-center text-sm text-secondary-red-100 font-semibold uppercase md:text-lg sm:text-md"
      >
        {{ blok.header }}
      </div>
      <h2 class="mx-auto mb-custom-lg text-3xl font-bold md:text-4xl">
        {{ blok.title }}
      </h2>
      <div>
        <TransitionGroup
          v-if="sortArticle.length > 0 && !notFound"
          :css="false"
          name="list"
          tag="div" class="grid gap-custom-lg 2xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2"
          @enter="onEnter"
          @leave="onLeave"
          @before-enter="onBeforeEnter"
        >
          <CardArticleCard
            v-for="(article, index) in sortArticle"
            :key="article.uuid"
            class="transition-all duration-300"
            :article="article.content"
            :slug="article.full_slug"
            :custom="article?.custom_link"
            :active-index="activeIndex"
            :date="article.created_at"
            :index="index"
            :loading="loading"
            :categories="categories"
            @mouseover="onHover(index)"
            @mouseleave="activeIndex = undefined"
          />
        </TransitionGroup>
        <div
          v-else
          class="relative w-full flex flex-col cursor-pointer justify-center rounded-lg bg-white px-4 text-center md:flex-row md:items-center"
        >
          <span class="text-xl text-secondary-gray-400 font-medium normal-case">{{ t('button.news_found') }}</span>
        </div>
      </div>
      <div class="mt-8 flex flex-wrap items-center gap-4">
        <div :class="page <= 1 ? 'opacity-50 cursor-not-allowed' : ''" class="group flex cursor-pointer items-center gap-1 border border-blue-600 rounded-md px-2.5 py-2" @click="nextArticle">
          <svg class="text-blue-600 transition-all ease group-hover:-translate-x-1" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"><path fill="currentColor" d="m6.523 12.5l3.735 3.735q.146.146.153.344t-.153.363q-.166.166-.357.169q-.191.002-.357-.163l-4.382-4.383q-.243-.242-.243-.565t.243-.565l4.382-4.383q.146-.146.347-.153t.367.159q.16.165.162.354t-.162.353L6.523 11.5h12.38q.214 0 .358.143t.143.357t-.143.357t-.357.143z" /></svg>
          <span class="text-sm text-blue-600 font-medium">Next</span>
        </div>
        <div :class="notFound ? 'opacity-50 cursor-not-allowed' : ''" class="group flex cursor-pointer items-center gap-1 border border-neutral-200 rounded-md bg-blue-600 px-2.5 py-2" @click="previousArticle">
          <span class="text-sm text-white font-medium">Previous</span>
          <svg class="text-white transition-all ease group-hover:translate-x-1" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"><path fill="currentColor" d="M17.073 12.5H5.5q-.213 0-.357-.143T5 12t.143-.357q.144-.143.357-.143h11.573l-3.735-3.735q-.146-.146-.152-.344t.152-.363q.166-.166.357-.169t.357.163l4.383 4.383q.13.13.183.267t.053.298t-.053.298t-.183.267l-4.383 4.383q-.146.146-.347.153t-.367-.159q-.16-.165-.162-.354t.162-.353z" /></svg>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="md:justify-normal relative z-10 w-full flex flex-col cursor-pointer justify-center rounded-lg bg-white p-5 transition duration-200 ease-linear">
      <UISpinner class="mx-auto min-h-lg lg:min-h-xl" />
    </div>
  </div>
</template>
